import React, { useState, useEffect } from 'react';
import api from '../../../utils/api';
import { format, parseISO } from 'date-fns';

const CompaniesPage = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [industryFilter, setIndustryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editingCompany, setEditingCompany] = useState(null);
  const [isAddingCompany, setIsAddingCompany] = useState(false);
  const [newCompany, setNewCompany] = useState({ 
    name: '', 
    industry: 'Retail', 
    users: 0, 
    status: 'Active',
    plan: 'Standard',
    contactEmail: '',
    storageUsed: '0%'
  });
  
  // Get unique industries for the filter dropdown
  const industries = [...new Set(companies.map(company => company.industry))];
  
  // Filter companies based on search term and filters
  const filteredCompanies = companies.filter(company => 
    (company.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
     company.contactEmail.toLowerCase().includes(searchTerm.toLowerCase())) &&
    (industryFilter === '' || company.industry === industryFilter) &&
    (statusFilter === '' || company.status === statusFilter)
  );
  
  const handleEdit = (company) => {
    setEditingCompany({ ...company });
  };
  
  const handleSaveEdit = () => {
    setCompanies(companies.map(company => company.id === editingCompany.id ? editingCompany : company));
    setEditingCompany(null);
  };
  
  // New admin user state - always required when creating a new company
  const [newAdminUser, setNewAdminUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: 'tempPassword123'
  });

  // Fetch companies data
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get('/api/companies');
        
        const formattedCompanies = response.data.map(company => ({
          id: company.id,
          name: company.name,
          slug: company.slug,
          industry: company.industry || 'N/A',
          users: company.userCount || 0,
          status: company.isActive ? 'Active' : 'Inactive',
          plan: company.subscription || 'FREE',
          lastPayment: company.subscriptionEndDate ? format(parseISO(company.subscriptionEndDate), 'yyyy-MM-dd') : 'N/A',
          contactEmail: company.settings?.contactEmail || '',
          storageUsed: company.settings?.storageUsed || '0%',
          address: company.address,
          city: company.city,
          state: company.state,
          zipCode: company.zipCode,
          country: company.country,
          phone: company.phone,
          website: company.website
        }));
        
        setCompanies(formattedCompanies);
      } catch (err) {
        console.error('Error fetching companies:', err);
        setError('Failed to load companies. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchCompanies();
  }, []);

  const handleAddCompany = async () => {
    try {
      setLoading(true);
      
  // Validate required fields
  if (!newCompany.name) {
    alert('Company name is required');
    setLoading(false);
    return;
  }
  
  // Validate required admin user fields
  if (!newAdminUser.firstName || !newAdminUser.lastName || !newAdminUser.email) {
    alert('Company Admin user information is required');
    setLoading(false);
    return;
  }
      
      // Generate slug from company name
      const slug = newCompany.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      
      // Prepare company data
      const companyData = {
        name: newCompany.name,
        slug,
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'USA',
        phone: '',
        website: '',
        industry: newCompany.industry,
        // Always include admin data - it's required
        adminData: {
          firstName: newAdminUser.firstName,
          lastName: newAdminUser.lastName,
          email: newAdminUser.email,
          password: newAdminUser.password
        }
      };
      
      const response = await api.post('/api/companies', companyData);
      
      // Add the new company to state
      const newCompanyData = {
        id: response.data.company.id,
        name: response.data.company.name,
        slug: response.data.company.slug,
        industry: newCompany.industry,
        users: response.data.adminUser ? 1 : 0,
        status: 'Active',
        plan: 'FREE',
        lastPayment: 'N/A',
        contactEmail: newAdminUser.email || '',
        storageUsed: '0%'
      };
      
      setCompanies([...companies, newCompanyData]);
      
      // Reset form
      setNewCompany({ 
        name: '', 
        industry: 'Retail', 
        users: 0, 
        status: 'Active',
        plan: 'Standard',
        contactEmail: '',
        storageUsed: '0%'
      });
      
      setNewAdminUser({
        firstName: '',
        lastName: '',
        email: '',
        password: ''
      });
      
      setIsAddingCompany(false);
    } catch (err) {
      console.error('Error adding company:', err);
      alert(err.response?.data?.message || 'Failed to add company. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteCompany = async (id) => {
    if (window.confirm('Are you sure you want to delete this company? This action cannot be undone and will remove all associated users and data.')) {
      try {
        setLoading(true);
        
        // Delete company
        await api.delete(`/api/companies/${id}`);
        
        // Update local state
        setCompanies(companies.filter(company => company.id !== id));
      } catch (err) {
        console.error('Error deleting company:', err);
        alert(err.response?.data?.message || 'Failed to delete company. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold">Company Management</h1>
        <button 
          onClick={() => setIsAddingCompany(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          Add New Company
        </button>
      </div>
      
      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">Search Companies</label>
            <input
              type="text"
              id="search"
              placeholder="Search by name or email"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="w-full md:w-48">
            <label htmlFor="industry-filter" className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
            <select
              id="industry-filter"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              value={industryFilter}
              onChange={(e) => setIndustryFilter(e.target.value)}
            >
              <option value="">All Industries</option>
              {industries.map((industry, index) => (
                <option key={index} value={industry}>{industry}</option>
              ))}
            </select>
          </div>
          <div className="w-full md:w-48">
            <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              id="status-filter"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Trial">Trial</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6">
          <span className="block sm:inline">{error}</span>
        </div>
      )}
      
      {/* Loading state */}
      {loading && (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <p className="text-gray-500">Loading companies...</p>
        </div>
      )}
      
      {/* Companies Table */}
      {!loading && companies.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <p className="text-gray-500">No companies found.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Industry</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Users</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Storage</th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredCompanies.map(company => (
              <tr key={company.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold">
                      {company.name.charAt(0)}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">{company.name}</div>
                      <div className="text-sm text-gray-500">{company.contactEmail}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">{company.industry}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{company.users}</div>
                  <button
                    onClick={() => window.location.href = `/app/admin/users?companyId=${company.id}`}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Manage Users
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium">
                    {company.plan}
                  </div>
                  <div className="text-xs text-gray-500">
                    Last payment: {company.lastPayment}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                    ${company.status === 'Active' ? 'bg-green-100 text-green-800' : 
                      company.status === 'Inactive' ? 'bg-red-100 text-red-800' : 
                      'bg-yellow-100 text-yellow-800'}`}
                  >
                    {company.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="w-24 h-2 bg-gray-200 rounded-full">
                    <div 
                      className={`h-full rounded-full ${
                        parseInt(company.storageUsed) > 80 ? 'bg-red-500' : 
                        parseInt(company.storageUsed) > 60 ? 'bg-yellow-500' : 'bg-green-500'
                      }`} 
                      style={{ width: company.storageUsed }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{company.storageUsed} used</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button 
                    onClick={() => handleEdit(company)}
                    className="text-primary-600 hover:text-primary-900 mr-4"
                  >
                    Edit
                  </button>
                  <button 
                    onClick={() => handleDeleteCompany(company.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      )}
      
      {/* Edit Company Modal */}
      {editingCompany && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-semibold mb-4">Edit Company</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                <input
                  type="text"
                  id="edit-name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={editingCompany.name}
                  onChange={(e) => setEditingCompany({...editingCompany, name: e.target.value})}
                />
              </div>
              <div>
                <label htmlFor="edit-email" className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                <input
                  type="email"
                  id="edit-email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={editingCompany.contactEmail}
                  onChange={(e) => setEditingCompany({...editingCompany, contactEmail: e.target.value})}
                />
              </div>
              <div>
                <label htmlFor="edit-industry" className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                <select
                  id="edit-industry"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={editingCompany.industry}
                  onChange={(e) => setEditingCompany({...editingCompany, industry: e.target.value})}
                >
                  {industries.map((industry, index) => (
                    <option key={index} value={industry}>{industry}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="edit-plan" className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                <select
                  id="edit-plan"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={editingCompany.plan}
                  onChange={(e) => setEditingCompany({...editingCompany, plan: e.target.value})}
                >
                  <option value="Free Trial">Free Trial</option>
                  <option value="Standard">Standard</option>
                  <option value="Professional">Professional</option>
                  <option value="Enterprise">Enterprise</option>
                </select>
              </div>
              <div>
                <label htmlFor="edit-status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  id="edit-status"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={editingCompany.status}
                  onChange={(e) => setEditingCompany({...editingCompany, status: e.target.value})}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Trial">Trial</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setEditingCompany(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Add New Company Modal */}
      {isAddingCompany && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <h2 className="text-xl font-semibold mb-4">Add New Company</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-3">Company Information</h3>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="new-name" className="block text-sm font-medium text-gray-700 mb-1">Company Name <span className="text-red-600">*</span></label>
                    <input
                      type="text"
                      id="new-name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      value={newCompany.name}
                      onChange={(e) => setNewCompany({...newCompany, name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="new-industry" className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                    <select
                      id="new-industry"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      value={newCompany.industry}
                      onChange={(e) => setNewCompany({...newCompany, industry: e.target.value})}
                    >
                      {industries.map((industry, index) => (
                        <option key={index} value={industry}>{industry}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-3">Company Admin User <span className="text-red-600">*</span></h3>
                <p className="text-sm text-gray-500 mb-3">Create an admin user who will manage this company.</p>
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="admin-first-name" className="block text-sm font-medium text-gray-700 mb-1">First Name <span className="text-red-600">*</span></label>
                      <input
                        type="text"
                        id="admin-first-name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        value={newAdminUser.firstName}
                        onChange={(e) => setNewAdminUser({...newAdminUser, firstName: e.target.value})}
                      />
                    </div>
                    <div>
                      <label htmlFor="admin-last-name" className="block text-sm font-medium text-gray-700 mb-1">Last Name <span className="text-red-600">*</span></label>
                      <input
                        type="text"
                        id="admin-last-name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        value={newAdminUser.lastName}
                        onChange={(e) => setNewAdminUser({...newAdminUser, lastName: e.target.value})}
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="admin-email" className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-600">*</span></label>
                    <input
                      type="email"
                      id="admin-email"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      value={newAdminUser.email}
                      onChange={(e) => setNewAdminUser({...newAdminUser, email: e.target.value})}
                    />
                  </div>
                  <div>
                    <label htmlFor="admin-password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                    <input
                      type="password"
                      id="admin-password"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      value={newAdminUser.password}
                      onChange={(e) => setNewAdminUser({...newAdminUser, password: e.target.value})}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setIsAddingCompany(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCompany}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                Add Company
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompaniesPage;
